/**
 * Input Component
 *
 * A reusable text input component that follows the StartupHive design system.
 *
 * Props:
 * - label: string - label text
 * - id: string - input id (required for accessibility)
 * - name: string - input name
 * - type: string - input type (default: 'text')
 * - value: string - controlled input value
 * - onChange: function - change handler
 * - placeholder: string
 * - required: boolean
 * - disabled: boolean
 * - error: string - error message to display
 * - helperText: string - helper text below input
 * - className: additional CSS classes for wrapper
 * - ...rest: any other input attributes
 */

function Input({
  label,
  id,
  name,
  type = 'text',
  value,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  error,
  helperText,
  className = '',
  ...rest
}) {
  const hasError = !!error

  return (
    <div className={`form-group ${className}`.trim()}>
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
          {required && <span className="text-error"> *</span>}
        </label>
      )}
      <input
        type={type}
        id={id}
        name={name || id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className={`input ${hasError ? 'input-error' : ''}`}
        aria-invalid={hasError}
        aria-describedby={error ? `${id}-error` : helperText ? `${id}-helper` : undefined}
        {...rest}
      />
      {error && (
        <p id={`${id}-error`} className="form-error">
          {error}
        </p>
      )}
      {helperText && !error && (
        <p id={`${id}-helper`} className="form-helper">
          {helperText}
        </p>
      )}
    </div>
  )
}

export default Input
